import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import useIsMobile from "../hooks/useIsMobile";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupIcon from "@mui/icons-material/Group";
import ChatIcon from "@mui/icons-material/Chat";
import PaymentIcon from "@mui/icons-material/Payment";
import LogoutIcon from "@mui/icons-material/Logout";
import logo from "../img/logo.png";

const Sidebar = ({ open, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logOut, isAdmin } = useUserAuth();
    const isMobile = useIsMobile();

    const navItems = [
        { label: "Dashboard", icon: <DashboardIcon fontSize="small" />, path: "/auth" },
        { label: "Chat", icon: <ChatIcon fontSize="small" />, path: "/chat" },
        ...(isAdmin ? [{ label: "Team", icon: <GroupIcon fontSize="small" />, path: "/team" }] : []),
        { label: "Billing", icon: <PaymentIcon fontSize="small" />, path: "/billing" },
    ];

    const handleLogout = async () => {
        try {
            await logOut();
            navigate("/");
        } catch (err) {
            console.error(err);
        }
    };

    const handleNav = (path) => {
        navigate(path);
        if (isMobile && onClose) onClose();
    };

    const isActive = (path) => {
        if (path === "/auth") return location.pathname === "/auth";
        return location.pathname.startsWith(path);
    };

    const sidebar = (
        <div style={{
            width: "240px",
            minHeight: "100vh",
            backgroundColor: "#1c0943",
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            ...(isMobile ? { position: "relative", zIndex: 1001 } : {}),
        }}>
            {/* Logo */}
            <div style={{ padding: "24px 20px 32px" }}>
                <img src={logo} alt="Logo" style={{ width: "100%", maxWidth: "140px" }} />
            </div>

            {/* Nav items */}
            <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px", padding: "0 12px" }}>
                {navItems.map((item) => {
                    const active = isActive(item.path);
                    return (
                        <button
                            key={item.path}
                            onClick={() => handleNav(item.path)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "10px 12px",
                                borderRadius: "8px",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: active ? 600 : 400,
                                color: active ? "#fff" : "rgba(255,255,255,0.6)",
                                backgroundColor: active ? "rgba(130,94,231,0.2)" : "transparent",
                                transition: "all 0.15s",
                                width: "100%",
                                textAlign: "left",
                            }}
                            onMouseEnter={(e) => {
                                if (!active) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.06)";
                            }}
                            onMouseLeave={(e) => {
                                if (!active) e.currentTarget.style.backgroundColor = "transparent";
                            }}
                        >
                            {item.icon}
                            {item.label}
                        </button>
                    );
                })}
            </nav>

            {/* Logout */}
            <div style={{ padding: "16px 12px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <button
                    onClick={handleLogout}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "14px",
                        color: "rgba(255,255,255,0.6)",
                        backgroundColor: "transparent",
                        width: "100%",
                        textAlign: "left",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.06)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                    <LogoutIcon fontSize="small" />
                    Log out
                </button>
            </div>
        </div>
    );

    // Desktop: always visible
    if (!isMobile) return sidebar;

    // Mobile: overlay with backdrop
    if (!open) return null;

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                display: "flex",
            }}
        >
            {sidebar}
            <div
                onClick={onClose}
                style={{
                    flex: 1,
                    backgroundColor: "rgba(0,0,0,0.4)",
                }}
            />
        </div>
    );
};

export default Sidebar;
